import { jsPDF } from "jspdf";
import { toast } from "react-toastify";
import { Capacitor } from "@capacitor/core";
import { Filesystem, Directory } from "@capacitor/filesystem";
import { Share } from "@capacitor/share";

type StockEntry = {
  id: number;
  instrument: { name: string; code?: string };
  quantity: number;
  parameters?: string;
  responsiblePerson: string;
  approvedBy: string;
  remark?: string;
};

type StockFormData = {
  stockNumber?: string;
  parameters?: string;
  stocks: StockEntry[];
};

export const downloadStockPDF = (data: StockFormData) => {
  if (!data?.stocks || data.stocks.length === 0) {
    toast.error("No stock data available");
    return;
  }

  // ✅ FINAL DEBUG — YE 100% BATAYEGA DATA AA KYA RAHA HAI
  console.log("✅ PDF FINAL DATA:", JSON.stringify(data, null, 2));

  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();

  const sideMargin = 10;
  const topMargin = 20;
  const bottomMargin = 20;

  const primaryColor = [0, 122, 90];
  let currentY = topMargin;

  const image = new Image();
  image.src = "/images/logo.png";

  image.onload = async () => {
    /* ================= LOGO ================= */
    const aspect = image.naturalWidth / image.naturalHeight;
    const logoHeight = 20;
    const logoWidth = logoHeight * aspect;
    doc.addImage(image, "PNG", sideMargin, topMargin, logoWidth, logoHeight);

    /* ================= COMPANY ================= */
    const companyY = topMargin + logoHeight + 6;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(14);
    doc.text("Quick Engineering Solutions Pvt. Ltd.", sideMargin, companyY);

    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    let txtY = companyY + 5;
    doc.text("Naya Nagar, CDF Industrial Area, Anopshahar Road,", sideMargin, txtY);
    txtY += 5;
    doc.text("Cherat, Aligarh, Uttar Pradesh 202122", sideMargin, txtY);
    txtY += 5;
    doc.text("Email: qenggsolutions@gmail.com", sideMargin, txtY);
    txtY += 5;
    doc.text("Phone: +91 8077774614", sideMargin, txtY);

    /* ================= TITLE ================= */
    doc.setFontSize(22);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...primaryColor);
    doc.text("STOCK ENTRY", pageWidth - sideMargin, topMargin + 15, {
      align: "right",
    });

    currentY = txtY + 15;

    /* ================= BASIC DETAILS ================= */
    const drawField = (label: string, value?: any) => {
      const safeValue =
        typeof value === "string" && value.trim() !== ""
          ? value
          : value !== undefined && value !== null
            ? String(value)
            : "N/A";

      doc.setFont("helvetica", "bold");
      doc.setFontSize(11);
      doc.setTextColor(0, 0, 0);
      doc.text(label, sideMargin, currentY);

      doc.setFont("helvetica", "normal");
      doc.text(safeValue, sideMargin + 70, currentY);

      currentY += 8;
    };

    drawField("Stock Order Number :", data.stockNumber);
    drawField("Responsible Person :", data.stocks[0]?.responsiblePerson);
    drawField("Approved By :", data.stocks[0]?.approvedBy);

    // ✅ ✅ FINAL PARAMETERS FORCE-FETCH
    const formParameters =
      (typeof data.parameters === "string" && data.parameters.trim()) ||
      (typeof data.stocks[0]?.parameters === "string" &&
        data.stocks[0].parameters.trim()) ||
      "N/A";

    drawField("Parameters :", formParameters);

    currentY += 6;

    /* ================= ITEMS TABLE ================= */
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);

    const tableWidth = pageWidth - sideMargin * 2;

    const colWidths = {
      sno: 15,
      item: 90,
      qty: 25,
      remark: tableWidth - (15 + 90 + 25),
    };

    const headerHeight = 10;
    const rowPadding = 6;

    /* ===== TABLE HEADER ===== */
    let x = sideMargin;

    doc.rect(x, currentY, colWidths.sno, headerHeight);
    doc.text("S.No", x + colWidths.sno / 2, currentY + 6, { align: "center" });
    x += colWidths.sno;

    doc.rect(x, currentY, colWidths.item, headerHeight);
    doc.text("Item", x + colWidths.item / 2, currentY + 6, { align: "center" });
    x += colWidths.item;

    doc.rect(x, currentY, colWidths.qty, headerHeight);
    doc.text("Quantity", x + colWidths.qty / 2, currentY + 6, { align: "center" });
    x += colWidths.qty;

    doc.rect(x, currentY, colWidths.remark, headerHeight);
    doc.text("Remark", x + colWidths.remark / 2, currentY + 6, { align: "center" });

    currentY += headerHeight;

    /* ===== TABLE ROWS ===== */
    doc.setFont("helvetica", "normal");

    data.stocks.forEach((s, i) => {
      const itemLines = doc.splitTextToSize(
        s.instrument?.name || "N/A",
        colWidths.item - 6
      );

      // ✅ ✅ FINAL REMARK FORCE-FETCH
      const remarkText =
        (typeof s.remark === "string" && s.remark.trim()) ||
        (typeof (s as any).remarks === "string" && (s as any).remarks.trim()) ||
        "-";

      const remarkLines = doc.splitTextToSize(
        remarkText,
        colWidths.remark - 6
      );

      const rowHeight =
        Math.max(itemLines.length, remarkLines.length) * rowPadding;

      x = sideMargin;

      doc.rect(x, currentY, colWidths.sno, rowHeight);
      doc.text(String(i + 1), x + colWidths.sno / 2, currentY + rowHeight / 2, {
        align: "center",
        baseline: "middle",
      });
      x += colWidths.sno;

      doc.rect(x, currentY, colWidths.item, rowHeight);
      doc.text(itemLines, x + colWidths.item / 2, currentY + rowHeight / 2, {
        align: "center",
        baseline: "middle",
      });
      x += colWidths.item;

      doc.rect(x, currentY, colWidths.qty, rowHeight);
      doc.text(String(s.quantity ?? "N/A"), x + colWidths.qty / 2, currentY + rowHeight / 2, {
        align: "center",
        baseline: "middle",
      });
      x += colWidths.qty;

      doc.rect(x, currentY, colWidths.remark, rowHeight);
      doc.text(remarkLines, x + colWidths.remark / 2, currentY + rowHeight / 2, {
        align: "center",
        baseline: "middle",
      });

      currentY += rowHeight;

      if (currentY > pageHeight - bottomMargin - 20) {
        doc.addPage();
        currentY = topMargin;
      }
    });

    /* ================= FOOTER ================= */
    const now = new Date();
    doc.setFontSize(8);
    doc.setFont("helvetica", "bold");
    doc.text(
      `Generated on: ${now.toLocaleDateString("en-GB")} ${now.toLocaleTimeString("en-GB")}`,
      sideMargin,
      pageHeight - bottomMargin
    );

    /* ================= UNIVERSAL DOWNLOAD (WEB + APK) ================= */

    const platform = Capacitor.getPlatform();

    const safeStock = String(data.stockNumber || "ENTRY").replace(
      /[^a-zA-Z0-9]/g,
      "_"
    );
    const fileName = `Stock_${safeStock}.pdf`;

    // 🌐 WEB
    if (platform === "web") {
      const blob = doc.output("blob");
      const reader = new FileReader();

      reader.onloadend = () => {
        const a = document.createElement("a");
        a.href = reader.result as string;
        a.download = fileName;
        a.target = "_blank";
        a.click();
      };

      reader.readAsDataURL(blob);
      toast.success("Stock PDF downloaded successfully!");
      return;
    }

    // 📱 ANDROID APK
    const base64 = doc.output("datauristring").split(",")[1];

    const result = await Filesystem.writeFile({
      path: `QESPL/${fileName}`,
      data: base64,
      directory: Directory.Documents,
      recursive: true,
    });

    await Share.share({
      title: fileName,
      text: "Stock Entry",
      url: result.uri,
      dialogTitle: "Share Stock PDF",
    });

    toast.success("PDF saved & ready to share!");
  };

  image.onerror = () => {
    toast.error("Logo image not found");
  };
};
